/*
 * File: store.ts
 * Project: rosita
 * -----
 * Last Modified: 18 Feb 2022 3:20:37 pm
 * -----
 * Copyright (c) 2022 Elegant Software Solutions All rights reserved.
 */
import { AnyAction, configureStore, ThunkDispatch } from '@reduxjs/toolkit';
import { setupListeners } from '@reduxjs/toolkit/dist/query';
import { TypedUseSelectorHook, useDispatch, useSelector } from 'react-redux';
import { abbyCadabbyApi } from './services/abbyCadabby';
import { sparingRequestConfig } from './services/calvin';
import { sparingRequestConfigTypes } from './services/calvin/sparingRequestConfigTypes';
import { chuckieSueAddressesApi, chuckieSueAddressesParams, chuckieSueWorldsApi } from './services/chuckieSue';
import { cookieMonsterApi } from './services/cookieMonster';
import { freddyCadabbyApi } from './services/freddyCadabby';
import { googleMapsApi } from './services/googleMaps/googleMapsApi';
import { hermanTheMopApi } from './services/hermanTheMop';
import { juliaApi, juliaProductsParams } from './services/julia';
import { mrJohnsonApi } from './services/mrJohnson/mrJohnsonApi';
import { mrsGrouchApi } from './services/mrsGrouch/mrsGrouchApi';
import { rudyCadabbyApi } from './services/rudyCadabby/rudyCadabbyApi';
import { sparingRequestsParams } from './services/twittlebugs/sparingParams';
import { sparingRequestApi } from './services/twittlebugs/sparingRequestsApi';
import { waybillApi } from './services/waybillApi';
import { appSlice, formSlice } from './slices';
import { attachmentSlice } from './slices/attachmentsSlice';
import { selectedSparingRequests } from './slices/selectedDeploymentRequestSlice';

export const store = configureStore({
  reducer: {
    [appSlice.name]: appSlice.reducer,
    [formSlice.name]: formSlice.reducer,
    [selectedSparingRequests.name]: selectedSparingRequests.reducer,
    [attachmentSlice.name]: attachmentSlice.reducer,
    // Elmo
    // Cookie Monster
    [cookieMonsterApi.reducerPath]: cookieMonsterApi.reducer,
    // Chuckie Sue
    [chuckieSueAddressesApi.reducerPath]: chuckieSueAddressesApi.reducer,
    [chuckieSueAddressesParams.name]: chuckieSueAddressesParams.reducer,
    [chuckieSueWorldsApi.reducerPath]: chuckieSueWorldsApi.reducer,
    // Dr Nobel Price
    // Julia
    [juliaApi.reducerPath]: juliaApi.reducer,
    [juliaProductsParams.name]: juliaProductsParams.reducer,
    // Abby Cadabby
    [abbyCadabbyApi.reducerPath]: abbyCadabbyApi.reducer,
    // Google Maps
    [googleMapsApi.reducerPath]: googleMapsApi.reducer,
    // Calvin
    [sparingRequestConfigTypes.reducerPath]: sparingRequestConfigTypes.reducer,
    [sparingRequestConfig.reducerPath]: sparingRequestConfig.reducer,
    // Freddy Cadabby
    [freddyCadabbyApi.reducerPath]: freddyCadabbyApi.reducer,
    // Rudy Cadabby
    [rudyCadabbyApi.reducerPath]: rudyCadabbyApi.reducer,
    // Mr Johnson
    [mrJohnsonApi.reducerPath]: mrJohnsonApi.reducer,
    // Substitutions
    [hermanTheMopApi.reducerPath]: hermanTheMopApi.reducer,
    // Twittlebugs
    [sparingRequestApi.reducerPath]: sparingRequestApi.reducer,
    [sparingRequestsParams.name]: sparingRequestsParams.reducer,
    // Waybill API
    [waybillApi.reducerPath]: waybillApi.reducer,
    // Mrs Grouch
    [mrsGrouchApi.reducerPath]: mrsGrouchApi.reducer
  },
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware().concat([
      chuckieSueAddressesApi.middleware,
      cookieMonsterApi.middleware,
      chuckieSueWorldsApi.middleware,
      juliaApi.middleware,
      abbyCadabbyApi.middleware,
      freddyCadabbyApi.middleware,
      rudyCadabbyApi.middleware,
      mrJohnsonApi.middleware,
      mrsGrouchApi.middleware,
      sparingRequestApi.middleware,
      sparingRequestConfigTypes.middleware,
      sparingRequestConfig.middleware,
      waybillApi.middleware,
      hermanTheMopApi.middleware,
      googleMapsApi.middleware
    ])
});

export type ReduxState = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch;
export type ThunkAppDispatch = ThunkDispatch<ReduxState, void, AnyAction>;

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export const useAppDispatch = () => useDispatch<AppDispatch>();
export const useAppSelector: TypedUseSelectorHook<ReduxState> = useSelector;

setupListeners(store.dispatch);
export default store;
